import { isNil, toNumber } from 'lodash';
import { ColumnsType } from 'antd/lib/table/interface';

import {
  AnticipatedScheduleTypeEnum,
  AssignmentInfoUserResponseModel,
  AssignmentPrimaryDocumentViewModel,
  AssignmentRateTypeEnum,
  AssignmentStatusTypeEnum,
  AssignmentTeammateSelectModel,
  AssignmentTerminationReasonTypeEnum,
  AssignmentTypeEnum,
  AssignmentTypeFilterEnum,
  CustomerMspOrVmsOverviewDto,
  EmploymentTypeEnum,
  JobCategoryEnum,
  MultiRateEnum,
  OvertimeEnum,
  PayTypeEnum,
  PreScreeningBilledToEnum,
  QuestionnaireModel,
  SelectTeammateViewModel,
  SplitTypeEnum,
  SummaryAssignmentDetailModel,
  TimeAMPMEnum,
  TimePointEnum,
  WorkFromHomeEnum,
} from 'api/backofficeApiClient';
import { getFullName } from './personUtils';
import {
  AssignmentBulkImportViewModel,
  AssignmentHistoryResponse,
  AssignmentInfoAdminResponseModel,
  AssignmentTeammateTypeEnum,
  AssignmentTerminationToKronosEnum,
  KronosInfoStatusEnum,
  RenewKindEnum,
  RenewStatusEnum,
  WorkerTypeEnum,
} from 'api/adminApiClient';
import { AssignmentCreationTypeEnum } from 'components/modals/AssignmentDataModal/enum';
import { AssignmentHiddenColumns } from 'features/assignment/enums/AssignmentHiddenColumns.enum';
import { InternalReportRecruiterTypeEnum } from 'enums';
import { FinancialInformationHistory } from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/AssignmentDetailsCard/Primary/FinancialInformationDetailsCard';
import { FinancialInfoCalculator } from 'components/modals/AssignmentDataModal/FinancialInfo/calculationUtils';
import { getDecimalSafeValue } from './mathUtils';
import { SimplePriceAgreementW2Info } from 'components/modals/AssignmentDataModal/FinancialInfo';
import {
  mapWorkerInfoToInitialValues,
  mapWorksiteLocationToInitialValues,
} from 'features/assignment/AssignmentDetailsPage/utils';
import {
  AssignmentBulkImportFormData,
  AssignmentBulkImportFormModel,
} from 'features/assignment/AssignmentBulkImportPage/AssignmentBulkImportTableSection';
import { AssignmentBulkImportModel } from 'features/assignment/AssignmentBulkImportPage/AssignmentBulkImportPage';
import { getWorkerType } from './workerUtils';
import { getFormattedNumber } from './numberUtils';
import { AssignmentActionEnum } from 'components/modals/AssignmentActionModal';
import moment from 'moment';
import { TabKeys } from '../components/modals/AssignmentDataModal/AssignmentModal/enum';

export type AssignmentDetailsModel =
  | AssignmentInfoAdminResponseModel
  | AssignmentInfoUserResponseModel;

const getEmploymentCategoryLabel = (status: JobCategoryEnum | undefined) => {
  switch (status) {
    case JobCategoryEnum.IT:
      return 'IT';
    case JobCategoryEnum.Clerical:
      return 'Clerical';
    case JobCategoryEnum.Executive:
      return 'Executive';
    case JobCategoryEnum.Engineering:
      return 'Engineering';
    case JobCategoryEnum.AccountingOrFinance:
      return 'Accounting/Finance';
    case JobCategoryEnum.WarehousingOrLightIndustrial:
      return 'Warehousing/Light Industrial';
    case JobCategoryEnum.Healthcare:
      return 'Healthcare';
    case JobCategoryEnum.Other:
      return 'Other';

    default:
      return '';
  }
};

const getEmploymentCategoryOptions = () => [
  {
    value: JobCategoryEnum.IT,
    label: getEmploymentCategoryLabel(JobCategoryEnum.IT),
  },
  {
    value: JobCategoryEnum.Clerical,
    label: getEmploymentCategoryLabel(JobCategoryEnum.Clerical),
  },
  {
    value: JobCategoryEnum.Executive,
    label: getEmploymentCategoryLabel(JobCategoryEnum.Executive),
  },
  {
    value: JobCategoryEnum.Engineering,
    label: getEmploymentCategoryLabel(JobCategoryEnum.Engineering),
  },
  {
    value: JobCategoryEnum.AccountingOrFinance,
    label: getEmploymentCategoryLabel(JobCategoryEnum.AccountingOrFinance),
  },
  {
    value: JobCategoryEnum.WarehousingOrLightIndustrial,
    label: getEmploymentCategoryLabel(
      JobCategoryEnum.WarehousingOrLightIndustrial,
    ),
  },
  {
    value: JobCategoryEnum.Healthcare,
    label: getEmploymentCategoryLabel(JobCategoryEnum.Healthcare),
  },
  {
    value: JobCategoryEnum.Other,
    label: getEmploymentCategoryLabel(JobCategoryEnum.Other),
  },
];

const getEmploymentTypeLabel = (status: EmploymentTypeEnum | undefined) => {
  switch (status) {
    case EmploymentTypeEnum.FullTime:
      return 'Full time';
    case EmploymentTypeEnum.PartTime:
      return 'Part time';

    default:
      return '';
  }
};

const getEmploymentTypeOptions = () => [
  {
    value: EmploymentTypeEnum.FullTime,
    label: getEmploymentTypeLabel(EmploymentTypeEnum.FullTime),
  },
  {
    value: EmploymentTypeEnum.PartTime,
    label: getEmploymentTypeLabel(EmploymentTypeEnum.PartTime),
  },
];

export const getAssignmentStatusLabel = (status: AssignmentStatusTypeEnum) => {
  switch (status) {
    case AssignmentStatusTypeEnum.Active:
      return 'Active';
    case AssignmentStatusTypeEnum.Request:
      return 'Pending';
    case AssignmentStatusTypeEnum.Completed:
      return 'Completed';
    case AssignmentStatusTypeEnum.Cancelled:
      return 'Cancelled';

    default:
      return '';
  }
};

export const getAssignmentStatusOptions = () => [
  {
    value: AssignmentStatusTypeEnum.Active,
    label: getAssignmentStatusLabel(AssignmentStatusTypeEnum.Active),
  },
  {
    value: AssignmentStatusTypeEnum.Request,
    label: getAssignmentStatusLabel(AssignmentStatusTypeEnum.Request),
  },
  {
    value: AssignmentStatusTypeEnum.Completed,
    label: getAssignmentStatusLabel(AssignmentStatusTypeEnum.Completed),
  },
  {
    value: AssignmentStatusTypeEnum.Cancelled,
    label: getAssignmentStatusLabel(AssignmentStatusTypeEnum.Cancelled),
  },
];

export const getAssignmentStatusClassName = (
  status: AssignmentStatusTypeEnum,
) => {
  switch (status) {
    case AssignmentStatusTypeEnum.Active:
      return 'Active';
    case AssignmentStatusTypeEnum.Request:
      return 'Assignment Pending';
    case AssignmentStatusTypeEnum.Completed:
      return 'Completed';
    case AssignmentStatusTypeEnum.Cancelled:
      return 'Cancelled';

    default:
      return '';
  }
};

const getTerminationAssignmentLabel = (
  type?: AssignmentTerminationReasonTypeEnum,
) => {
  switch (type) {
    case AssignmentTerminationReasonTypeEnum.Converted:
      return 'Converted';
    case AssignmentTerminationReasonTypeEnum.AssignmentCancelled:
      return 'Assignment Did Not Start';
    case AssignmentTerminationReasonTypeEnum.Resigned:
      return 'Resigned';
    case AssignmentTerminationReasonTypeEnum.AttendanceIssue:
      return 'Attendance Issue';
    case AssignmentTerminationReasonTypeEnum.PerformanceIssue:
      return 'Performance Issue';
    case AssignmentTerminationReasonTypeEnum.MisconductIssue:
      return 'Misconduct Issue';
    case AssignmentTerminationReasonTypeEnum.AssignmentComplete:
      return 'Assignment Complete';

    default:
      return '';
  }
};

const getAssignmentTerminationReasonOptions = () => [
  {
    value: AssignmentTerminationReasonTypeEnum.AssignmentComplete,
    label: getTerminationAssignmentLabel(
      AssignmentTerminationReasonTypeEnum.AssignmentComplete,
    ),
  },
  {
    value: AssignmentTerminationReasonTypeEnum.Converted,
    label: getTerminationAssignmentLabel(
      AssignmentTerminationReasonTypeEnum.Converted,
    ),
  },
  {
    value: AssignmentTerminationReasonTypeEnum.Resigned,
    label: getTerminationAssignmentLabel(
      AssignmentTerminationReasonTypeEnum.Resigned,
    ),
  },
  {
    value: AssignmentTerminationReasonTypeEnum.AttendanceIssue,
    label: getTerminationAssignmentLabel(
      AssignmentTerminationReasonTypeEnum.AttendanceIssue,
    ),
  },
  {
    value: AssignmentTerminationReasonTypeEnum.PerformanceIssue,
    label: getTerminationAssignmentLabel(
      AssignmentTerminationReasonTypeEnum.PerformanceIssue,
    ),
  },
  {
    value: AssignmentTerminationReasonTypeEnum.MisconductIssue,
    label: getTerminationAssignmentLabel(
      AssignmentTerminationReasonTypeEnum.MisconductIssue,
    ),
  },
  {
    value: AssignmentTerminationReasonTypeEnum.AssignmentCancelled,
    label: getTerminationAssignmentLabel(
      AssignmentTerminationReasonTypeEnum.AssignmentCancelled,
    ),
  },
];

const getAnticipateScheduleLabel = (type?: AnticipatedScheduleTypeEnum) => {
  switch (type) {
    case AnticipatedScheduleTypeEnum.From0800AmTo0500Pm:
      return '8:00 AM - 5:00 PM';
    case AnticipatedScheduleTypeEnum.From0700AmTo0400Pm:
      return '7:00 AM - 4:00 PM';
    case AnticipatedScheduleTypeEnum.From0900AmTo0600Pm:
      return '9:00 AM - 6:00 PM';
    case AnticipatedScheduleTypeEnum.Other:
      return 'Other';
    case AnticipatedScheduleTypeEnum.NotKnown:
      return 'Not known';
    case AnticipatedScheduleTypeEnum.NineEighty:
      return '9/80';
    case AnticipatedScheduleTypeEnum.FourTen:
      return '4/10';

    default:
      return '';
  }
};

const getAnticipateScheduleOptions = () => [
  {
    value: AnticipatedScheduleTypeEnum.From0800AmTo0500Pm,
    label: getAnticipateScheduleLabel(
      AnticipatedScheduleTypeEnum.From0800AmTo0500Pm,
    ),
  },
  {
    value: AnticipatedScheduleTypeEnum.From0700AmTo0400Pm,
    label: getAnticipateScheduleLabel(
      AnticipatedScheduleTypeEnum.From0700AmTo0400Pm,
    ),
  },
  {
    value: AnticipatedScheduleTypeEnum.From0900AmTo0600Pm,
    label: getAnticipateScheduleLabel(
      AnticipatedScheduleTypeEnum.From0900AmTo0600Pm,
    ),
  },
  {
    value: AnticipatedScheduleTypeEnum.Other,
    label: getAnticipateScheduleLabel(AnticipatedScheduleTypeEnum.Other),
  },
  {
    value: AnticipatedScheduleTypeEnum.NotKnown,
    label: getAnticipateScheduleLabel(AnticipatedScheduleTypeEnum.NotKnown),
  },
  {
    value: AnticipatedScheduleTypeEnum.NineEighty,
    label: getAnticipateScheduleLabel(AnticipatedScheduleTypeEnum.NineEighty),
  },
  {
    value: AnticipatedScheduleTypeEnum.FourTen,
    label: getAnticipateScheduleLabel(AnticipatedScheduleTypeEnum.FourTen),
  },
];

const getAnticipateOtherHoursLabel = (type: TimePointEnum) => {
  switch (type) {
    case TimePointEnum.T01H00M:
      return '1:00';
    case TimePointEnum.T02H00M:
      return '2:00';
    case TimePointEnum.T03H00M:
      return '3:00';
    case TimePointEnum.T04H00M:
      return '4:00';
    case TimePointEnum.T05H00M:
      return '5:00';
    case TimePointEnum.T06H00M:
      return '6:00';
    case TimePointEnum.T07H00M:
      return '7:00';
    case TimePointEnum.T08H00M:
      return '8:00';
    case TimePointEnum.T09H00M:
      return '9:00';
    case TimePointEnum.T10H00M:
      return '10:00';
    case TimePointEnum.T11H00M:
      return '11:00';
    case TimePointEnum.T12H00M:
      return '12:00';

    default:
      return '';
  }
};

const getAnticipateOtherHoursOptions = () => [
  {
    value: TimePointEnum.T01H00M,
    label: getAnticipateOtherHoursLabel(TimePointEnum.T01H00M),
  },
  {
    value: TimePointEnum.T02H00M,
    label: getAnticipateOtherHoursLabel(TimePointEnum.T02H00M),
  },
  {
    value: TimePointEnum.T03H00M,
    label: getAnticipateOtherHoursLabel(TimePointEnum.T03H00M),
  },
  {
    value: TimePointEnum.T04H00M,
    label: getAnticipateOtherHoursLabel(TimePointEnum.T04H00M),
  },
  {
    value: TimePointEnum.T05H00M,
    label: getAnticipateOtherHoursLabel(TimePointEnum.T05H00M),
  },
  {
    value: TimePointEnum.T06H00M,
    label: getAnticipateOtherHoursLabel(TimePointEnum.T06H00M),
  },
  {
    value: TimePointEnum.T07H00M,
    label: getAnticipateOtherHoursLabel(TimePointEnum.T07H00M),
  },
  {
    value: TimePointEnum.T08H00M,
    label: getAnticipateOtherHoursLabel(TimePointEnum.T08H00M),
  },
  {
    value: TimePointEnum.T09H00M,
    label: getAnticipateOtherHoursLabel(TimePointEnum.T09H00M),
  },
  {
    value: TimePointEnum.T10H00M,
    label: getAnticipateOtherHoursLabel(TimePointEnum.T10H00M),
  },
  {
    value: TimePointEnum.T11H00M,
    label: getAnticipateOtherHoursLabel(TimePointEnum.T11H00M),
  },
  {
    value: TimePointEnum.T12H00M,
    label: getAnticipateOtherHoursLabel(TimePointEnum.T12H00M),
  },
];

const getDayTimeLabel = (type: TimeAMPMEnum) => {
  switch (type) {
    case TimeAMPMEnum.AM:
      return 'AM';
    case TimeAMPMEnum.PM:
      return 'PM';

    default:
      return '';
  }
};

const getDayTimeOptions = () => [
  {
    value: TimeAMPMEnum.AM,
    label: getDayTimeLabel(TimeAMPMEnum.AM),
  },
  {
    value: TimeAMPMEnum.PM,
    label: getDayTimeLabel(TimeAMPMEnum.PM),
  },
];

const getOvertimeLabel = (type?: OvertimeEnum) => {
  switch (type) {
    case OvertimeEnum.Exempt:
      return 'Exempt';
    case OvertimeEnum.Nonexempt:
      return 'Nonexempt';

    default:
      return '';
  }
};

const getOvertimeOptions = () => [
  {
    value: OvertimeEnum.Exempt,
    label: getOvertimeLabel(OvertimeEnum.Exempt),
  },
  {
    value: OvertimeEnum.Nonexempt,
    label: getOvertimeLabel(OvertimeEnum.Nonexempt),
  },
];

const getPayTypeLabel = (type?: PayTypeEnum) => {
  switch (type) {
    case PayTypeEnum.Hourly:
      return 'Hourly';
    case PayTypeEnum.Salary:
      return 'Salary';

    default:
      return '';
  }
};

const getPayTypeOptions = () => [
  {
    value: PayTypeEnum.Hourly,
    label: getPayTypeLabel(PayTypeEnum.Hourly),
  },
  {
    value: PayTypeEnum.Salary,
    label: getPayTypeLabel(PayTypeEnum.Salary),
  },
];

const getWorkFromHomeLabel = (type?: WorkFromHomeEnum) => {
  switch (type) {
    case WorkFromHomeEnum.HomeOnly:
      return 'Work from home only';
    case WorkFromHomeEnum.OnsiteOnly:
      return 'Onsite only';
    case WorkFromHomeEnum.OnsitePotentialForTravel:
      return 'Onsite w/ potential for travel';
    case WorkFromHomeEnum.Hybrid:
      return 'Hybrid (Onsite and WFH)';
    case WorkFromHomeEnum.Unknown:
      return 'Unknown';

    default:
      return '';
  }
};

const getWorkFromHomeOptions = () => [
  {
    label: getWorkFromHomeLabel(WorkFromHomeEnum.HomeOnly),
    value: WorkFromHomeEnum.HomeOnly,
  },
  {
    label: getWorkFromHomeLabel(WorkFromHomeEnum.OnsiteOnly),
    value: WorkFromHomeEnum.OnsiteOnly,
  },
  {
    label: getWorkFromHomeLabel(WorkFromHomeEnum.OnsitePotentialForTravel),
    value: WorkFromHomeEnum.OnsitePotentialForTravel,
  },
  {
    label: getWorkFromHomeLabel(WorkFromHomeEnum.Hybrid),
    value: WorkFromHomeEnum.Hybrid,
  },
  {
    label: getWorkFromHomeLabel(WorkFromHomeEnum.Unknown),
    value: WorkFromHomeEnum.Unknown,
  },
];

const getPreScreeningBilledToLabel = (type?: PreScreeningBilledToEnum) => {
  switch (type) {
    case PreScreeningBilledToEnum.Member:
      return 'Member';
    case PreScreeningBilledToEnum.Customer:
      return 'Customer';

    default:
      return '';
  }
};

const getPreScreeningBilledToOptions = () => [
  {
    value: PreScreeningBilledToEnum.Member,
    label: getPreScreeningBilledToLabel(PreScreeningBilledToEnum.Member),
  },
  {
    value: PreScreeningBilledToEnum.Customer,
    label: getPreScreeningBilledToLabel(PreScreeningBilledToEnum.Customer),
  },
];

const getQuestionnaireField = (questionnaire?: QuestionnaireModel) => {
  const value = getQuestionnaireFieldValue(questionnaire?.isValue);
  const comment = getQuestionnaireFieldComment(questionnaire?.comment);

  return { value, comment };
};

const getQuestionnaireFieldValue = (value?: boolean) => {
  if (isNil(value)) return 'N/A';

  return value ? 'Yes' : 'No';
};

const getQuestionnaireFieldComment = (comment?: string) => {
  return comment || 'No comment';
};

const getSplitTypeLabel = (splitType?: SplitTypeEnum) => {
  switch (splitType) {
    case SplitTypeEnum.NoSplit:
      return 'No split';

    case SplitTypeEnum.ExistingPartner:
      return 'With existing partner';

    case SplitTypeEnum.NewPartner:
      return 'With new partner';

    default:
      return '';
  }
};

const getModalTitle = (type: AssignmentCreationTypeEnum) => {
  switch (type) {
    case AssignmentCreationTypeEnum.Create:
    case AssignmentCreationTypeEnum.Template:
      return 'Create new assignment';
    case AssignmentCreationTypeEnum.Renew:
      return 'Assignment renewal';
    case AssignmentCreationTypeEnum.Rehire:
      return 'Assignment rehire ';
    case AssignmentCreationTypeEnum.EditRehire:
      return 'Edit rehire request';
    case AssignmentCreationTypeEnum.EditDraft:
      return 'Edit assignment draft';
    case AssignmentCreationTypeEnum.Edit:
      return 'Edit assignment';
    case AssignmentCreationTypeEnum.EditRenew:
      return 'Edit renewal request';
    case AssignmentCreationTypeEnum.EditTemplate:
      return 'Edit assignment template';

    default:
      return '';
  }
};

const getModalSubmitName = (type: AssignmentCreationTypeEnum) => {
  switch (type) {
    case AssignmentCreationTypeEnum.Create:
    case AssignmentCreationTypeEnum.Template:
    case AssignmentCreationTypeEnum.EditDraft:
      return 'Create';
    case AssignmentCreationTypeEnum.Renew:
    case AssignmentCreationTypeEnum.Edit:
    case AssignmentCreationTypeEnum.EditRenew:
    case AssignmentCreationTypeEnum.Rehire:
    case AssignmentCreationTypeEnum.EditRehire:
      return 'Update';
    case AssignmentCreationTypeEnum.EditTemplate:
      return 'Save';

    default:
      return '';
  }
};

const getContactLabel = (primaryContact: {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  title?: string;
}) => {
  const { firstName, lastName, email, phoneNumber, title } = primaryContact;
  const fullName = getFullName(firstName, lastName);

  if (!fullName && !email) {
    if (title) return title;
    if (phoneNumber) return phoneNumber;
  }

  return [fullName, email].filter(Boolean).join(': ');
};

const getAssignmentTypeLabel = (
  assignmentType?: AssignmentTypeEnum | AssignmentTypeFilterEnum,
) => {
  switch (assignmentType) {
    case AssignmentTypeEnum.ICAssignment:
      return 'IC';

    case AssignmentTypeEnum.EEAssignment:
      return 'W2';

    case AssignmentTypeEnum.SCAssignment:
      return 'Subcontract';

    default:
      return '';
  }
};

const getAssignmentTypeFilterLabel = (
  assignmentType?: AssignmentTypeFilterEnum,
) => {
  const type = getAssignmentTypeLabel(assignmentType);

  return type || 'All';
};

const isMemberAdditionalAssignmentInfoSectionEnabled = (
  assignmentDetails?: AssignmentDetailsModel,
) => {
  if (!assignmentDetails) return false;

  return (
    assignmentDetails.assignmentType === AssignmentTypeEnum.ICAssignment &&
    !!(
      assignmentDetails.questionnaireInfo ||
      assignmentDetails.contractorQuestionnaireInfo
    )
  );
};

const getFilterLabel = (option: AssignmentTypeFilterEnum): string => {
  switch (option) {
    case AssignmentTypeFilterEnum.All:
      return 'All';

    default:
      return '';
  }
};

const getAssignmentsTableColumns = (
  columns: ColumnsType<SummaryAssignmentDetailModel>,
  isCorporateTA?: boolean,
) => {
  if (!isCorporateTA) return columns;

  return columns.filter(
    (item) =>
      item.key !== AssignmentHiddenColumns.Customer &&
      item.key !== AssignmentHiddenColumns.CustomerId,
  );
};

const isDraftOrCompletedAssignment = (status: AssignmentStatusTypeEnum) => {
  return (
    status === AssignmentStatusTypeEnum.Completed ||
    status === AssignmentStatusTypeEnum.Draft
  );
};

interface PageIdLabelOptions {
  renewStatus?: RenewStatusEnum;
  assignmentStatus?: AssignmentStatusTypeEnum;
  isTemplate?: boolean;
  isPartner?: boolean;
  renewKind?: RenewKindEnum;
}
const getAssignmentPageIdLabel = (options: PageIdLabelOptions) => {
  const { renewStatus, assignmentStatus, isTemplate, isPartner, renewKind } =
    options;

  if (isTemplate) return 'Assignment template';

  if (isPartner) return 'Partner assignment';

  switch (assignmentStatus) {
    case AssignmentStatusTypeEnum.Request:
      if (renewStatus) {
        return renewKind === RenewKindEnum.Renew
          ? 'Assignment renewal details'
          : 'Assignment rehire details';
      }

      return 'Assignment request details';

    case AssignmentStatusTypeEnum.Active:
    case AssignmentStatusTypeEnum.Completed:
    case AssignmentStatusTypeEnum.Cancelled:
      return 'Assignment details';

    case AssignmentStatusTypeEnum.Draft:
      return 'Draft assignment details';

    default:
      return '';
  }
};

const getAssignmentTimesheetApprovers = (
  assignmentDocument:
    | AssignmentPrimaryDocumentViewModel
    | AssignmentHistoryResponse,
) => {
  const { timesheetApproverInfo } = assignmentDocument;
  const additionalTimesheetApprovers =
    assignmentDocument?.additionalTimesheetApprovers || [];

  if (!timesheetApproverInfo) return [];

  const timesheetApprovers = [
    timesheetApproverInfo,
    ...additionalTimesheetApprovers,
  ];

  return timesheetApprovers;
};

export enum PartnerSplitEnum {
  SplitPercent,
  PaymentPerHour,
}

const getPartnerSplitOptionLabel = (split: PartnerSplitEnum) => {
  switch (split) {
    case PartnerSplitEnum.SplitPercent:
      return 'Split %';
    case PartnerSplitEnum.PaymentPerHour:
      return 'Payment per hour ($)';
    default:
      return '';
  }
};

const partnerSplitOptions = [
  {
    value: PartnerSplitEnum.SplitPercent,
    label: getPartnerSplitOptionLabel(PartnerSplitEnum.SplitPercent),
  },
  {
    value: PartnerSplitEnum.PaymentPerHour,
    label: getPartnerSplitOptionLabel(PartnerSplitEnum.PaymentPerHour),
  },
];

const getPartnerProfitSplit = (splitPercent?: number, splitAmount?: number) => {
  if (!isNil(splitPercent)) {
    return `${splitPercent.toFixed(2)}%`;
  }

  if (!isNil(splitAmount)) {
    return `$${getFormattedNumber(splitAmount, {
      separator: ',',
      toFixed: 2,
    })}`;
  }

  return '';
};

const getRateTypeLabel = (rateType: AssignmentRateTypeEnum) => {
  switch (rateType) {
    case AssignmentRateTypeEnum.Money:
      return '$';
    case AssignmentRateTypeEnum.Percent:
      return '%';
  }
};

const getInternalReportingRateLabel = (
  recruiter: AssignmentTeammateSelectModel,
) => {
  const { rate, rateType } = recruiter;

  if (isNil(rate) || isNil(rateType)) return '';

  const typeLabel = getRateTypeLabel(rateType);
  const rateValue = getFormattedNumber(rate, {
    separator: ',',
    toFixed: 2,
  });

  return rateType === AssignmentRateTypeEnum.Money
    ? `${typeLabel}${rateValue}`
    : `${rateValue}${typeLabel}`;
};

const getKronosStatusName = (status?: KronosInfoStatusEnum) => {
  switch (status) {
    case KronosInfoStatusEnum.AwaitingStatus:
      return 'UKG: Awaiting Status';
    case KronosInfoStatusEnum.Synced:
      return 'UKG: Synced';
    case KronosInfoStatusEnum.SyncedWithErrors:
      return 'UKG: Synced with errors';
    case KronosInfoStatusEnum.SyncFailed:
      return 'UKG: Sync failed';
    case KronosInfoStatusEnum.ReprocessedManually:
      return 'UKG: Reprocessed Manually';
    default:
      return '';
  }
};

const getAssignmentTerminationStatusName = (
  status?: AssignmentTerminationToKronosEnum,
) => {
  switch (status) {
    case AssignmentTerminationToKronosEnum.InProgress:
      return 'UKG: Termination Awaiting';
    case AssignmentTerminationToKronosEnum.Success:
      return 'UKG: Terminated';
    case AssignmentTerminationToKronosEnum.Fail:
      return 'UKG: Termination Failed';
    default:
      return '';
  }
};

const internalReportingOptions = [
  {
    value: AssignmentRateTypeEnum.Percent,
    label: getRateTypeLabel(AssignmentRateTypeEnum.Percent),
  },
  {
    value: AssignmentRateTypeEnum.Money,
    label: getRateTypeLabel(AssignmentRateTypeEnum.Money),
  },
];

const getTeammateFullNameLabel = (teammate: AssignmentTeammateSelectModel) => {
  switch (teammate.teammateType) {
    case AssignmentTeammateTypeEnum.ExternalRecruiter:
      return teammate.externalFullName;
    case AssignmentTeammateTypeEnum.Teammate:
      return getFullName(teammate.firstName, teammate.lastName);
    case AssignmentTeammateTypeEnum.PendingTeammate:
      return teammate.pendingTeammateEmail + ' (Pending)';
  }
};

const getTeammateValueByTeammateType = (teammate: SelectTeammateViewModel) => {
  switch (teammate.teammateType) {
    case AssignmentTeammateTypeEnum.Teammate:
      return {
        value: teammate.userId,
        label: getFullName(teammate.firstName, teammate.lastName),
      };
    case AssignmentTeammateTypeEnum.PendingTeammate:
      return { value: teammate.email, label: teammate.email + ' (Pending)' };
  }
};

const getTeammateNameForInitialValues = (
  teammate: AssignmentTeammateSelectModel,
) => {
  switch (teammate.teammateType) {
    case AssignmentTeammateTypeEnum.PendingTeammate:
      return teammate.pendingTeammateEmail;
    case AssignmentTeammateTypeEnum.Teammate:
      return teammate.userId;
    case AssignmentTeammateTypeEnum.ExternalRecruiter:
      return InternalReportRecruiterTypeEnum.ExternalRecruiter;
  }
};

const putSignOfNumberFrontOfSymbol = (number: number, symbol: string = '$') =>
  +number >= 0 ? symbol : '-' + symbol;

const getWorkState = (
  assignment: AssignmentHistoryResponse,
  isW2Assignment: boolean,
) => {
  if (assignment?.workSameAsHomeOrBusinessLocation) {
    return isW2Assignment
      ? assignment?.workerInfo?.location?.state
      : assignment?.contractorCompanyInfo?.location?.state;
  }
  return assignment?.workLocation?.state;
};

const checkHasFinancialErrors = (
  assignment: AssignmentHistoryResponse,
  isW2Assignment: boolean,
  w2InfoByWorkSiteWorkLocation?: SimplePriceAgreementW2Info,
) => {
  const { mspOrVmsOverviewInfo } = assignment;
  const workSiteWorkLocation = getWorkState(assignment, isW2Assignment);

  return (
    (!!toNumber(mspOrVmsOverviewInfo?.mspFee) &&
      !!mspOrVmsOverviewInfo?.vmsFee) ||
    !!w2InfoByWorkSiteWorkLocation ||
    isNil(workSiteWorkLocation) ||
    !isW2Assignment
  );
};

const calculateFinancialInfoHistory = (
  assignment: AssignmentHistoryResponse,
): FinancialInformationHistory => {
  const {
    priceAgreement,
    assignmentType,
    mspOrVmsOverviewInfo,
    financialInfo,
  } = assignment;

  const isW2Assignment = assignmentType === AssignmentTypeEnum.EEAssignment;
  const w2InfoList = priceAgreement?.w2InfoList;
  const workerInfo = mapWorkerInfoToInitialValues(assignment);
  const worksiteLocation = mapWorksiteLocationToInitialValues(assignment);

  const w2InfoByWorkSiteWorkLocation =
    FinancialInfoCalculator.w2InfoByWorkSiteWorkLocation(
      w2InfoList,
      isW2Assignment,
      workerInfo,
      worksiteLocation,
    );

  const hasFinancialErrors = checkHasFinancialErrors(
    assignment,
    isW2Assignment,
    w2InfoByWorkSiteWorkLocation,
  );

  const getSafeComputedValue = (value: number) => {
    return hasFinancialErrors ? 0 : getDecimalSafeValue(value);
  };

  const vmsMspRate = getSafeComputedValue(
    FinancialInfoCalculator.getVmsMspRateValue(mspOrVmsOverviewInfo),
  );

  const vmsMspPortion = getSafeComputedValue(
    FinancialInfoCalculator.getVmsMspPortionValue(
      financialInfo?.billRate,
      mspOrVmsOverviewInfo,
    ),
  );

  const mbpRate =
    financialInfo?.mbpRate ||
    FinancialInfoCalculator.getMBPRateValue(
      isW2Assignment,
      priceAgreement?.icPrice,
      w2InfoByWorkSiteWorkLocation?.w2Price,
    );

  const markupPercent = getSafeComputedValue(
    FinancialInfoCalculator.getMarkupPercentValue(financialInfo),
  );

  const mbpPortion = getSafeComputedValue(
    FinancialInfoCalculator.getMbpPortionValue(financialInfo?.payRate, mbpRate),
  );

  const marginPercent = getSafeComputedValue(
    FinancialInfoCalculator.getMarginPercentValue(
      {
        billRate: financialInfo?.billRate,
        payType: financialInfo?.payType,
        mbpRate: mbpRate,
      },
      mspOrVmsOverviewInfo,
    ),
  );

  return {
    vmsMspRate: vmsMspRate,
    vmsMspPortion: vmsMspPortion,
    mbpRate: mbpRate,
    mbpPortion: mbpPortion,
    marginPercent: marginPercent,
    markupPercent: markupPercent,
  };
};

const getPayRateTypeLabel = (type?: MultiRateEnum) => {
  switch (type) {
    case MultiRateEnum.ProjectRate:
      return {
        title: 'Project Rate',
        subTitle: 'worked hours at different rate',
        description:
          'ex. Hours for project 1 paid at $20, hours for project 2 paid at $30, travel or training time at different rate',
      };
    case MultiRateEnum.ShiftRa:
      return {
        title: 'Shift Rate',
        subTitle:
          'worked hours automatically paid at different rate based on schedule',
        description:
          'ex. Overnight hours worked 7pm- 7am or hours worked on weekends paid at $1 more per hour, time worked on holiday is paid at 1.5x',
      };
    case MultiRateEnum.OnCall:
      return {
        title: 'On Call',
        subTitle: 'not working but paid at different rate',
        description:
          'ex. Must remain available to be called to work but is not actively working during this time. On Call time is not included in OT calculations',
      };
    case MultiRateEnum.PaidTimeOff:
      return {
        title: 'Paid Time Off',
        subTitle: 'not working hours, paid at regular pay rate',
        description: 'ex. Shift Cancellation, member or customer provided PTO',
      };
    case MultiRateEnum.TaxableAmount:
      return {
        title: 'Taxable Amount',
        subTitle: 'lump sum paid, not worked hours',
        description: 'ex. Bonus, commission, taxable relocation expenses',
      };
    case MultiRateEnum.NotTaxedAmount:
      return {
        title: 'Not taxed Amount',
        subTitle:
          'lump sum paid, not worked hours (requires backup to be paid)',
        description:
          'ex. Expense reimbursements with receipts or per diem under GSA allowance',
      };
  }
};

export const mapAssignmentBulkImportToRequest = (
  allAssignments: AssignmentBulkImportModel[],
  data: AssignmentBulkImportFormModel[],
): AssignmentBulkImportViewModel[] => {
  return allAssignments.map((assignment) => {
    const matchedAssignment = data.find((c) => c.id === assignment.id);
    assignment.workerFileKey = !isNil(matchedAssignment?.workerFileKey)
      ? matchedAssignment?.workerFileKey[0]
      : undefined;

    const { id, ...details } = assignment;
    return details;
  });
};

export const mapAssignmentBulkImportToInitialValues = (
  allAssignments: AssignmentBulkImportModel[],
): AssignmentBulkImportFormData => {
  const assignments = getListOfAssignmentsWithWorkerFile(allAssignments).map(
    (assignment) => {
      return {
        ...assignment,
        workerFileKey: !isNil(assignment.workerFileKey)
          ? [assignment.workerFileKey]
          : [],
      };
    },
  );
  return { assignments };
};

const getListOfAssignmentsWithWorkerFile = (
  assignments: AssignmentBulkImportModel[],
): AssignmentBulkImportModel[] => {
  return assignments.filter(
    (assignment) => getWorkerType(assignment.workerType) !== WorkerTypeEnum.EE,
  );
};

interface FinancialWarningMessagesOptions {
  mspVmsValues?: CustomerMspOrVmsOverviewDto;
  hasW2InfoByWorkSiteWorkLocation: boolean;
}

const getFinancialWarningMessages = (
  options: FinancialWarningMessagesOptions,
) => {
  const { mspVmsValues, hasW2InfoByWorkSiteWorkLocation } = options;

  const warningMessages = [];

  // Invalid if both msp and vms fields are provided
  if (!!toNumber(mspVmsValues?.mspFee) && !!mspVmsValues?.vmsFee) {
    warningMessages.push(
      'Invalid customer rates specified. Please contact support.',
    );
  }

  // Invalid if state value from worksite work location doesn't match
  // any placementStates from pricing agreement for W2 assignment
  if (!hasW2InfoByWorkSiteWorkLocation) {
    warningMessages.push(
      "The work location belongs to the state that wasn't specified in your pricing agreement. Please contact us.",
    );
  }

  return warningMessages;
};

const getActionTitleModal = (actionType?: AssignmentActionEnum) => {
  switch (actionType) {
    case AssignmentActionEnum.Edit:
      return 'Edit Assignment';
    case AssignmentActionEnum.Renew:
      return 'Renew Assignment';
    case AssignmentActionEnum.Terminate:
      return 'Terminate Assignment';
    case AssignmentActionEnum.Rehire:
      return 'Rehire Assignment';
    default:
      return '';
  }
};

const getActionTitle = (actionType: AssignmentActionEnum) => {
  switch (actionType) {
    case AssignmentActionEnum.Edit:
      return 'Edit';
    case AssignmentActionEnum.Renew:
      return 'Renew';
    case AssignmentActionEnum.Terminate:
      return 'Terminate';
    case AssignmentActionEnum.Rehire:
      return 'Rehire';
    default:
      return '';
  }
};

const getActionIcon = (actionType: AssignmentActionEnum) => {
  switch (actionType) {
    case AssignmentActionEnum.Edit:
      return 'icon icon-edit-modal';
    case AssignmentActionEnum.Renew:
      return 'icon icon-renew';
    case AssignmentActionEnum.Terminate:
      return 'icon icon-terminate';
    case AssignmentActionEnum.Rehire:
      return 'icon icon-rehire';
    default:
      return '';
  }
};

const getActionDescription = (actionType: AssignmentActionEnum) => {
  switch (actionType) {
    case AssignmentActionEnum.Edit:
      return 'Contacts, work site, and pay updates.';
    case AssignmentActionEnum.Renew:
      return 'Extend the Assignment for a longer duration.';
    case AssignmentActionEnum.Terminate:
      return 'Expected & unexpected close out of Assignment.';
    case AssignmentActionEnum.Rehire:
      return 'Rehire a Worker for a longer duration.';
    default:
      return '';
  }
};

const isTerminationDateInFuture = (terminationDate?: Date) => {
  const today = moment.utc();
  const termination = moment.utc(terminationDate);

  return termination.isAfter(today);
};

const getCorrectRenewedStatus = (
  isAssignmentRehired: boolean,
  isAssignmentRehiredWithChangeCustomer: boolean,
) => {
  if (isAssignmentRehired) return 'rehired';

  return isAssignmentRehiredWithChangeCustomer
    ? 'rehired change customer'
    : 'renewed';
};

const getCorrectRenewedIcon = (
  isAssignmentRehired: boolean,
  isAssignmentRehiredWithChangeCustomer: boolean,
) => {
  if (isAssignmentRehired) return 'icon icon-clock';

  return isAssignmentRehiredWithChangeCustomer
    ? 'icon icon-person'
    : 'icon icon-calendar-label';
};

interface AssignmentState {
  isTypeSelectorStepVisible?: boolean;
  isEditRenewMode?: boolean;
  isEditRehireMode?: boolean;
  isTemplateEditMode?: boolean;
}

const determineInitialTab = (params: AssignmentState): TabKeys => {
  const {
    isTypeSelectorStepVisible,
    isEditRenewMode,
    isEditRehireMode,
    isTemplateEditMode,
  } = params;

  if (
    (isTypeSelectorStepVisible && !isEditRenewMode && !isEditRehireMode) ||
    isTemplateEditMode
  ) {
    return TabKeys.TypeSelector;
  }

  return TabKeys.WorkerInfo;
};

interface AssignmentParams {
  isCreateMode?: boolean;
  isAdminRole?: boolean;
  isEditMode?: boolean;
  hasActiveAssignmentRestriction?: boolean;
  isRenewMode?: boolean;
  isRehireMode?: boolean;
}

const shouldShowTypeSelector = (params: AssignmentParams) => {
  const {
    hasActiveAssignmentRestriction,
    isCreateMode,
    isAdminRole,
    isEditMode,
    isRenewMode,
    isRehireMode,
  } = params;

  if (
    hasActiveAssignmentRestriction ||
    ((isRenewMode || isRehireMode) && isAdminRole)
  )
    return false;

  return isCreateMode || isAdminRole || isEditMode;
};

export {
  getCorrectRenewedStatus,
  getCorrectRenewedIcon,
  getEmploymentCategoryLabel,
  getEmploymentCategoryOptions,
  getEmploymentTypeLabel,
  getEmploymentTypeOptions,
  getAnticipateScheduleOptions,
  getAnticipateOtherHoursOptions,
  getDayTimeOptions,
  getOvertimeOptions,
  getPayTypeOptions,
  getPreScreeningBilledToOptions,
  getWorkFromHomeLabel,
  getWorkFromHomeOptions,
  getPreScreeningBilledToLabel,
  getAnticipateScheduleLabel,
  getOvertimeLabel,
  getPayTypeLabel,
  getSplitTypeLabel,
  getModalTitle,
  getModalSubmitName,
  getQuestionnaireField,
  getContactLabel,
  getAssignmentTypeLabel,
  getAssignmentTypeFilterLabel,
  isMemberAdditionalAssignmentInfoSectionEnabled,
  getFilterLabel,
  getAssignmentsTableColumns,
  isDraftOrCompletedAssignment,
  getAssignmentPageIdLabel,
  getAssignmentTimesheetApprovers,
  partnerSplitOptions,
  getPartnerProfitSplit,
  getRateTypeLabel,
  getKronosStatusName,
  internalReportingOptions,
  getInternalReportingRateLabel,
  getTeammateFullNameLabel,
  getTeammateValueByTeammateType,
  getTeammateNameForInitialValues,
  putSignOfNumberFrontOfSymbol,
  getAnticipateOtherHoursLabel,
  getDayTimeLabel,
  calculateFinancialInfoHistory,
  getPayRateTypeLabel,
  getFinancialWarningMessages,
  getListOfAssignmentsWithWorkerFile,
  getAssignmentTerminationStatusName,
  getTerminationAssignmentLabel,
  getAssignmentTerminationReasonOptions,
  getActionTitleModal,
  getActionTitle,
  getActionIcon,
  getActionDescription,
  isTerminationDateInFuture,
  determineInitialTab,
  shouldShowTypeSelector,
};
